import {
  DailyExpenseDay,
  DailyExpenseLineInput,
  ExpenseCategory,
  ExpenseGroup,
} from "../../types/daily-expense.type";
import { formatNumber } from "../../utils";
import { roundHalfUp } from "../../utils/money";

/**
 * The daily form's model and its rules (handbook S2): which category goes where, what a cell
 * shows, the live totals, the validation, and the lines บันทึกร่าง sends. Pure, so the form, the
 * head-office drawer and the tests read one set of rules.
 */

/** Where each daily category sits on the form, in the server's sort order. */
export interface CategoryLayout {
  /** ฝ่ายขาย, one column per category, one row per route. */
  salesColumns: ExpenseCategory[];
  /** ฝ่ายบริหาร, one field each for the whole branch. */
  adminFields: ExpenseCategory[];
  /** อื่นๆ: the one category whose group the branch picks per line. */
  other: ExpenseCategory | null;
}

export const layoutOf = (categories: ExpenseCategory[]): CategoryLayout => {
  const sorted = [...categories].sort((left, right) => left.sort_order - right.sort_order);
  return {
    salesColumns: sorted.filter((category) => category.expense_group === "SALES"),
    adminFields: sorted.filter((category) => category.expense_group === "ADMIN"),
    other: sorted.find((category) => category.expense_group === null) ?? null,
  };
};

export const GROUP_LABEL: Record<ExpenseGroup, string> = {
  SALES: "ฝ่ายขาย",
  ADMIN: "ฝ่ายบริหาร",
};

/** One per-route amount as typed. */
export interface AmountCell {
  text: string;
  /** The แจ้งยอดโอน value the cell started from; null = keyed by hand. */
  prefillAmount: number | null;
}

/** One "อื่นๆ" row. Empty strings are an unpicked group / no route. */
export interface OtherRow {
  id: number;
  group: ExpenseGroup | "";
  routeUuid: string;
  description: string;
  amountText: string;
}

export interface DailyExpenseFormState {
  /** route uuid → category code → cell. */
  sales: Record<string, Record<string, AmountCell>>;
  /** category code → typed amount. */
  admin: Record<string, string>;
  others: OtherRow[];
}

const AMOUNT_PATTERN = /^\d+(\.\d{1,2})?$/;
const THOUSANDS_SEPARATOR = /,/g;

const cleanAmount = (text: string) => text.replace(THOUSANDS_SEPARATOR, "").trim();

/** Blank, or a non-negative amount with at most 2 decimals (thousands separators allowed). */
export const isValidAmountText = (text: string): boolean => {
  const cleaned = cleanAmount(text);
  return cleaned === "" || AMOUNT_PATTERN.test(cleaned);
};

/** The amount a cell counts for in the totals: blank and invalid text count as 0. */
export const parseAmount = (text: string): number => {
  const cleaned = cleanAmount(text);
  return AMOUNT_PATTERN.test(cleaned) ? Number(cleaned) : 0;
};

export const formatAmount = (amount: number): string => formatNumber(amount);

/** A valid amount re-written as 1,234.50 once the user leaves the field; anything else is kept. */
export const tidyAmountText = (text: string): string =>
  cleanAmount(text) !== "" && isValidAmountText(text) ? formatAmount(parseAmount(text)) : text;

const isBlank = (text: string) => cleanAmount(text) === "";

/** The user typed over the แจ้งยอดโอน value: the form flags it and shows the original. */
export const isCellEdited = (cell: AmountCell): boolean =>
  cell.prefillAmount !== null &&
  !isBlank(cell.text) &&
  isValidAmountText(cell.text) &&
  parseAmount(cell.text) !== cell.prefillAmount;

export const cellKey = (routeUuid: string, categoryCode: string) =>
  `${routeUuid}|${categoryCode}`;

/**
 * The form as the day stands. A cell shows its saved line; with `withPrefill` a pre-filled cell
 * that has no line yet shows the route's แจ้งยอดโอน value. Only an editable day takes the prefill:
 * a read-only day shows exactly the lines its totals were made of.
 */
export const buildFormState = (
  day: DailyExpenseDay,
  layout: CategoryLayout,
  withPrefill: boolean,
): DailyExpenseFormState => {
  const sales: DailyExpenseFormState["sales"] = {};
  day.routes.forEach((route) => {
    sales[route.route_uuid] = {};
    layout.salesColumns.forEach(({ code }) => {
      const line = day.lines.find(
        (candidate) =>
          candidate.category_code === code && candidate.route_uuid === route.route_uuid,
      );
      const prefill = withPrefill ? route.prefill?.[code] : undefined;
      if (line) {
        sales[route.route_uuid][code] = {
          text: formatAmount(line.amount),
          prefillAmount: line.prefill_amount,
        };
      } else {
        sales[route.route_uuid][code] = {
          text: prefill === undefined ? "" : formatAmount(prefill),
          prefillAmount: prefill ?? null,
        };
      }
    });
  });

  const admin: DailyExpenseFormState["admin"] = {};
  layout.adminFields.forEach(({ code }) => {
    const line = day.lines.find((candidate) => candidate.category_code === code);
    admin[code] = line ? formatAmount(line.amount) : "";
  });

  const others = day.lines
    .filter((line) => line.category_code === layout.other?.code)
    .map<OtherRow>((line, index) => ({
      id: index + 1,
      group: line.expense_group,
      routeUuid: line.route_uuid ?? "",
      description: line.description ?? "",
      amountText: formatAmount(line.amount),
    }));

  return { sales, admin, others };
};

export const nextOtherId = (others: OtherRow[]): number =>
  others.reduce((highest, row) => Math.max(highest, row.id), 0) + 1;

export const emptyOtherRow = (id: number): OtherRow => ({
  id,
  group: "",
  routeUuid: "",
  description: "",
  amountText: "",
});

export interface DailyExpenseFormTotals {
  /** route uuid → its row of the ฝ่ายขาย table. */
  routeTotals: Record<string, number>;
  /** category code → its column of the ฝ่ายขาย table. */
  columnTotals: Record<string, number>;
  /** The ฝ่ายขาย table's corner cell. */
  salesTableTotal: number;
  salesTotal: number;
  adminTotal: number;
  total: number;
}

const sumOf = (amounts: number[]) => amounts.reduce((sum, amount) => sum + amount, 0);

/**
 * The live totals, rounded half-up to the satang the way the server rounds them: ฝ่ายขาย and
 * ฝ่ายบริหาร each, then รวม from the two rounded figures. An "อื่นๆ" row counts once a group is picked.
 */
export const computeFormTotals = (
  form: DailyExpenseFormState,
  layout: CategoryLayout,
): DailyExpenseFormTotals => {
  const routeUuids = Object.keys(form.sales);
  const routeTotals: Record<string, number> = {};
  routeUuids.forEach((routeUuid) => {
    routeTotals[routeUuid] = roundHalfUp(
      sumOf(Object.values(form.sales[routeUuid]).map((cell) => parseAmount(cell.text))),
    );
  });
  const columnTotals: Record<string, number> = {};
  layout.salesColumns.forEach(({ code }) => {
    columnTotals[code] = roundHalfUp(
      sumOf(routeUuids.map((routeUuid) => parseAmount(form.sales[routeUuid][code]?.text ?? ""))),
    );
  });
  const salesTableSum = sumOf(
    routeUuids.flatMap((routeUuid) =>
      Object.values(form.sales[routeUuid]).map((cell) => parseAmount(cell.text)),
    ),
  );
  const otherSumOf = (group: ExpenseGroup) =>
    sumOf(
      form.others
        .filter((row) => row.group === group)
        .map((row) => parseAmount(row.amountText)),
    );
  const salesTotal = roundHalfUp(salesTableSum + otherSumOf("SALES"));
  const adminTotal = roundHalfUp(
    sumOf(Object.values(form.admin).map(parseAmount)) + otherSumOf("ADMIN"),
  );
  return {
    routeTotals,
    columnTotals,
    salesTableTotal: roundHalfUp(salesTableSum),
    salesTotal,
    adminTotal,
    total: roundHalfUp(salesTotal + adminTotal),
  };
};

export interface OtherRowErrors {
  group: boolean;
  description: boolean;
  amount: boolean;
}

export interface DailyExpenseFormErrors {
  /** cellKey(route, code) of every invalid ฝ่ายขาย cell. */
  cells: Set<string>;
  /** Category codes of the invalid ฝ่ายบริหาร fields. */
  admin: Set<string>;
  /** "อื่นๆ" row id → what is wrong with it; rows without a fault are absent. */
  others: Record<number, OtherRowErrors>;
  /** Fields at fault, for the banner. */
  count: number;
}

/** Amounts ≥ 0 with at most 2 decimals; an "อื่นๆ" row needs a group and a description. */
export const validateForm = (form: DailyExpenseFormState): DailyExpenseFormErrors => {
  const cells = new Set<string>();
  Object.entries(form.sales).forEach(([routeUuid, row]) => {
    Object.entries(row).forEach(([code, cell]) => {
      if (!isValidAmountText(cell.text)) cells.add(cellKey(routeUuid, code));
    });
  });
  const admin = new Set(
    Object.entries(form.admin)
      .filter(([, text]) => !isValidAmountText(text))
      .map(([code]) => code),
  );
  const others: Record<number, OtherRowErrors> = {};
  let otherFaults = 0;
  form.others.forEach((row) => {
    const rowErrors: OtherRowErrors = {
      group: row.group === "",
      description: row.description.trim() === "",
      amount: !isValidAmountText(row.amountText),
    };
    const faults = Object.values(rowErrors).filter(Boolean).length;
    if (faults > 0) others[row.id] = rowErrors;
    otherFaults += faults;
  });
  return { cells, admin, others, count: cells.size + admin.size + otherFaults };
};

/**
 * The body of บันทึกร่าง: every cell the user means to keep, since the server replaces the whole
 * day. A blank pre-filled cell is sent as 0 — left out, it would come back as its แจ้งยอดโอน
 * value — while a blank hand-keyed cell is simply not a line. Call on a valid form only.
 */
export const buildSaveLines = (
  form: DailyExpenseFormState,
  layout: CategoryLayout,
): DailyExpenseLineInput[] => {
  const salesLines = Object.entries(form.sales).flatMap(([routeUuid, row]) =>
    Object.entries(row)
      .filter(([, cell]) => !isBlank(cell.text) || cell.prefillAmount !== null)
      .map(([code, cell]) => ({
        category_code: code,
        route_uuid: routeUuid,
        amount: parseAmount(cell.text),
      })),
  );
  const adminLines = Object.entries(form.admin)
    .filter(([, text]) => !isBlank(text))
    .map(([code, text]) => ({ category_code: code, amount: parseAmount(text) }));
  const otherCode = layout.other?.code;
  const otherLines = otherCode
    ? form.others.flatMap((row) =>
        row.group === ""
          ? []
          : [
              {
                category_code: otherCode,
                expense_group: row.group,
                // Only a ฝ่ายขาย row may name a route; ฝ่ายบริหาร belongs to the whole branch.
                ...(row.group === "SALES" && row.routeUuid ? { route_uuid: row.routeUuid } : {}),
                amount: parseAmount(row.amountText),
                description: row.description.trim(),
              },
            ],
      )
    : [];
  return [...salesLines, ...adminLines, ...otherLines];
};
